import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// Shared split-panel frame for the auth screens (login, signup). Mirrors the
// layout of the Little Verses (poems) reader's AuthShell — brand panel on
// the left that collapses away on phones, form on the right, which is the
// only thing that renders below `lg`. Restyled to this app's own navy/
// coral/teal/gold identity rather than the poems project's green one.
export default function AuthShell({ title, subtitle, children, footer, testId, tagline, taglineBody }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-hv-cream grid lg:grid-cols-2" data-testid={testId}>
      {/* Brand panel — decorative, so it's the half that goes away on small
          screens rather than shrinking the form. */}
      <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-hv-navy to-[#0E1B38] text-white p-12 relative overflow-hidden">
        <div className="relative z-10">
          <button onClick={() => navigate("/")} className="flex items-center gap-3 text-left" data-testid="auth-brand-link">
            <span className="bg-white rounded-xl p-1.5 shadow-sm shrink-0">
              <img src="/brand/hikmahverses-mark.png" alt="HikmahVerses" className="h-8 w-auto object-contain" />
            </span>
            <span>
              <span className="hv-heading block text-white text-lg leading-none">HikmahVerses</span>
              <span className="block text-[11px] uppercase tracking-[0.15em] text-hv-gold font-bold mt-1">From Cradle to Grave</span>
            </span>
          </button>
        </div>

        <div className="relative z-10">
          <p className="hv-heading text-3xl leading-snug text-white">{tagline}</p>
          <p className="mt-4 text-white/75 leading-relaxed max-w-sm">{taglineBody}</p>
        </div>

        <p className="relative z-10 text-xs uppercase tracking-[0.22em] text-white/50 font-bold">
          Made for families
        </p>
      </div>

      {/* Form side */}
      <div className="flex flex-col items-center justify-center px-5 sm:px-10 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          {/* Logo repeats here because the brand panel is hidden on phones. */}
          <button onClick={() => navigate("/")} className="lg:hidden flex items-center gap-3 mb-8" data-testid="auth-brand-link-mobile">
            <span className="bg-white rounded-xl p-1.5 shadow-sm shrink-0">
              <img src="/brand/hikmahverses-mark.png" alt="HikmahVerses" className="h-7 w-auto object-contain" />
            </span>
            <span className="text-left">
              <span className="hv-heading block text-hv-navy text-lg leading-none">HikmahVerses</span>
              <span className="block text-[11px] uppercase tracking-[0.15em] text-hv-teal font-bold mt-1">From Cradle to Grave</span>
            </span>
          </button>

          <h1 className="hv-heading text-3xl sm:text-4xl text-hv-navy">{title}</h1>
          {subtitle && <p className="mt-2 text-hv-slate leading-relaxed">{subtitle}</p>}

          <div className="mt-8">{children}</div>

          {footer && <div className="mt-6 text-center text-sm text-hv-slate">{footer}</div>}
        </motion.div>
      </div>
    </div>
  );
}

// A labelled input with a leading icon — same field treatment for every auth
// page. `htmlFor`/`id` aren't used here since the input is passed in as
// children (matches the pattern already in Login.js/Register.js).
export function AuthField({ icon, label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-bold text-hv-navy mb-1.5">{label}</span>
      <span className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border-2 border-[#E5DFC8] focus-within:border-hv-teal transition min-h-[52px]">
        <span className="text-hv-teal shrink-0">{icon}</span>
        {children}
      </span>
      {hint && <span className="block text-xs text-hv-slate mt-1.5">{hint}</span>}
    </label>
  );
}

export const authInputClass = "w-full bg-transparent outline-none text-base text-hv-navy placeholder:text-hv-slate/50";
